import { useQuery } from '@tanstack/react-query';
import { Form, useFormikContext } from 'formik';
import { CSSProperties, useState } from 'react';

import { ContractState, EffectiveDateMode } from '../../../types/ContractMessage';
import { PaymentTermReference } from '../../../types/PaymentTermReference';
import { Button } from '../../cdl/Button/Button';
import { Callout } from '../../cdl/Callout/Callout';
import { DrawerContentLayout } from '../../cdl/Drawer/DrawerContentLayout';
import { useDrawer } from '../../cdl/Drawer/useDrawer.hook';
import { FormikInput } from '../../cdl/Input/FormikInput';
import { Label } from '../../cdl/Label/Label';
import { FormikRadioGroup } from '../../cdl/RadioGroup/FormikRadioGroup';
import { Description, Radio, RadioGroupItem } from '../../cdl/RadioGroup/RadioGroup';
import { ReadOnlyField } from '../../cdl/ReadOnlyField/ReadOnlyField';
import { FormikSwitch } from '../../cdl/Switch/FormikSwitch';
import { Text } from '../../cdl/Text/Text';
import { FormikTextArea } from '../../cdl/TextArea/FormikTextArea';
import { queries } from '../../common/api/queryKeys/queries';
import { TextButton } from '../../common/buttons/TextButton';
import { FormikAssignedSupplierSelect } from '../../common/form-elements/formik/FormikAssignedSupplierSelect';
import { FormikCustomerCompanyMultiSelect } from '../../common/form-elements/formik/FormikCustomerCompanyMultiSelect';
import { FormikCustomerGroupSelect } from '../../common/form-elements/formik/FormikCustomerGroupSelect';
import { FormikDatePicker } from '../../common/form-elements/formik/FormikDatePicker';
import { FormikDebug } from '../../common/form-elements/formik/FormikDebug';
import { FormikEnumComboBox } from '../../common/form-elements/formik/FormikEnumComboBox';
import { FormikFormattedNumberInput } from '../../common/FormattedNumberInput/FormikFormattedNumberInput';
import { translate } from '../../common/helpers/translate.helper';
import { Characters } from '../../common/helpers/unicode.helper';
import { useOpenFreshchat } from '../../common/hooks/useOpenFreshchat';
import { useRole } from '../../common/hooks/useRole';
import { IconArrowRight } from '../../common/icons/cdl/ArrowRight';
import { InteractiveTranslation } from '../../common/InteractiveTranslation';
import { Box } from '../../common/ui/Box';
import { ContractVesselAccessDrawer } from '../../contracts/common/components/ContractVesselAccessDrawer';
import { useContractVesselAccessData } from '../../contracts/common/hooks/useContractVesselAccessData';
import { getPublishedContractState } from '../../contracts/form/contractForm.helpers';
import { ContractFormValues } from '../../contracts/form/contractForm.types';
import { FormikPriceListSection } from '../ContractForm/PriceListSection/FormikPriceListSection';

const dimmedStyle: CSSProperties = { opacity: 0.2, pointerEvents: 'none' };

interface ContractDrawerFormProps {
    isDraftSubmitting?: boolean;
    isSupplierReadOnly?: boolean;
    showSaveDraftButton?: boolean;
    showPublishButton?: boolean;
    showAwardButton?: boolean;
    showCustomerGroupSelect?: boolean;
    showPriceLists?: boolean;
}

export const ContractDrawerForm = ({
    isDraftSubmitting,
    isSupplierReadOnly = false,
    showSaveDraftButton,
    showPublishButton,
    showAwardButton,
    showCustomerGroupSelect,
    showPriceLists,
}: ContractDrawerFormProps) => {
    const { values, setFieldValue, submitForm, isSubmitting, dirty, handleSubmit } =
        useFormikContext<ContractFormValues>();
    const [isPriceListBusy, setIsPriceListBusy] = useState(false);
    const [isPriceListEditing, setIsPriceListEditing] = useState(false);
    const { push, pop } = useDrawer();
    const role = useRole();
    const openFreshchat = useOpenFreshchat();
    const isSuperAdmin = role.isSuperAdmin();
    const roleCustomerIds = role.getCompanies().map((company) => company.id);
    const groupCustomersQuery = useQuery({
        ...queries.customers.list({ groupId: values.customerGroupId }),
        enabled: isSuperAdmin && !!values.customerGroupId,
        staleTime: Infinity,
    });
    const isCustomerGroupLoading = groupCustomersQuery.isLoading || groupCustomersQuery.isFetching;
    const groupCustomerIds = groupCustomersQuery.data?.map((company) => company.id) ?? [];
    let customerIdsForAssignedSuppliers = roleCustomerIds;
    if (isSuperAdmin) {
        if (!values.customerGroupId) {
            customerIdsForAssignedSuppliers = [];
        } else if (values.customerIds.length) {
            customerIdsForAssignedSuppliers = values.customerIds;
        } else {
            customerIdsForAssignedSuppliers = groupCustomerIds;
        }
    }
    const priceListGroupId = isSuperAdmin ? values.customerGroupId : role.group.id;
    const showFormContent = !isSuperAdmin || !!values.customerGroupId;
    const isEditFlow = !showPublishButton;
    const showSaveDraftAction = showPublishButton || showSaveDraftButton;
    const showAwardAction = showPublishButton || showAwardButton;
    const isBusy = isSubmitting || !!isDraftSubmitting || isPriceListBusy;
    const vesselAccessData = useContractVesselAccessData({
        customerGroupId: values.customerGroupId,
        customerIds: values.customerIds,
        enabled: values.excludedVesselIds.length > 0,
    });
    const vesselNamesById = new Map(vesselAccessData.vessels.map((vessel) => [vessel.id, vessel.name]));
    const excludedVesselNames = values.excludedVesselIds
        .map((vesselId) => vesselNamesById.get(vesselId))
        .filter((vesselName): vesselName is string => !!vesselName)
        .join(', ');
    const submitWithState = async (state: ContractState) => {
        if (isPriceListEditing || isBusy) {
            return;
        }
        await setFieldValue('state', state, false);
        await submitForm();
    };

    const openVesselAccessDrawer = () => {
        const drawerId = push({
            header: { title: translate('contracts.vesselAccess.title') },
            content: (
                <ContractVesselAccessDrawer
                    customerGroupId={values.customerGroupId}
                    customerIds={values.customerIds}
                    excludedVesselIds={values.excludedVesselIds}
                    onSubmit={(excludedVesselIds) => {
                        setFieldValue('excludedVesselIds', excludedVesselIds, false);
                        pop(drawerId);
                    }}
                />
            ),
        });
    };

    const handlePublishSubmit = async () => {
        await submitWithState(getPublishedContractState(values.validFrom));
    };

    return (
        <Form
            style={{ height: '100%', minHeight: 0 }}
            onSubmit={(event) => {
                if (isPriceListEditing || isBusy) {
                    event.preventDefault();
                    return;
                }
                handleSubmit(event);
            }}
        >
            <DrawerContentLayout
                hasUnsavedChanges={dirty}
                isBusy={isBusy}
                actionsDisabled={isPriceListEditing || isPriceListBusy}
                secondaryAction={
                    showSaveDraftAction ? (
                        <Button
                            type="button"
                            onClick={() => submitWithState(ContractState.DRAFT)}
                            isLoading={!!isDraftSubmitting}
                            disabled={isSubmitting}
                        >
                            {translate('contracts.form.actions.saveDraft')}
                        </Button>
                    ) : null
                }
                primaryAction={
                    showAwardAction ? (
                        <Button
                            emphasis="high"
                            type="button"
                            onClick={() => handlePublishSubmit()}
                            isLoading={isSubmitting && !isDraftSubmitting}
                            disabled={isSubmitting || !!isDraftSubmitting}
                        >
                            {translate('contracts.form.actions.awardContract')}
                        </Button>
                    ) : (
                        <Button emphasis="high" type="submit" isLoading={isSubmitting} disabled={isDraftSubmitting}>
                            {translate(
                                isEditFlow
                                    ? 'contracts.form.actions.updateContract'
                                    : 'contracts.form.actions.saveContract'
                            )}
                        </Button>
                    )
                }
            >
                <Box display="flex" flexDirection="column" gap={48}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        rowGap={48}
                        inert={isPriceListEditing || isPriceListBusy}
                        style={isPriceListEditing ? dimmedStyle : undefined}
                    >
                        {showCustomerGroupSelect && isSuperAdmin ? (
                            <Box display="flex" flexDirection="column" gap={16}>
                                <FormikCustomerGroupSelect
                                    name="customerGroupId"
                                    label={translate('customerGroupSelect.label')}
                                    placeholder={translate('customerGroupSelect.placeholder')}
                                    onGroupChange={() => {
                                        setFieldValue('supplierId', '');
                                        setFieldValue('customerIds', []);
                                        setFieldValue('excludedVesselIds', []);
                                    }}
                                />
                            </Box>
                        ) : null}

                        {showFormContent ? (
                            <>
                                <Box display="flex" flexDirection="column" gap={16}>
                                    <Text as="h2" variant="subtitle1">
                                        {translate('contracts.form.sections.baseDetails')}
                                    </Text>
                                    <FormikInput
                                        name="name"
                                        label={translate('contracts.form.contractName')}
                                        description={translate('contracts.form.contractNameDescription')}
                                    />
                                    <FormikInput
                                        name="contractReference"
                                        label={translate('contracts.form.contractReference')}
                                        description={translate('contracts.form.contractReferenceDescription')}
                                    />
                                    <FormikTextArea
                                        name="description"
                                        label={translate('contracts.form.additionalInformation')}
                                        description={translate('contracts.form.additionalInformationDescription')}
                                        placeholder={translate('contracts.form.additionalInformationPlaceholder')}
                                        rows={4}
                                    />
                                </Box>

                                <Box display="flex" flexDirection="column" gap={16}>
                                    <Text as="h2" variant="subtitle1">
                                        {translate('contracts.form.sections.supplier')}
                                    </Text>
                                    {isEditFlow ? (
                                        isSupplierReadOnly ? (
                                            <ReadOnlyField
                                                label={translate('contracts.form.assignedSupplier')}
                                                value={values.supplierName}
                                            />
                                        ) : (
                                            <FormikAssignedSupplierSelect
                                                name="supplierId"
                                                customerIds={customerIdsForAssignedSuppliers}
                                                label={translate('contracts.form.assignedSupplier')}
                                                description={translate('contracts.form.assignedSupplierDescription')}
                                                placeholder={translate('contracts.form.assignedSupplierPlaceholder')}
                                                disabled={
                                                    isSuperAdmin
                                                        ? !values.customerGroupId ||
                                                          isCustomerGroupLoading ||
                                                          !customerIdsForAssignedSuppliers.length
                                                        : !customerIdsForAssignedSuppliers.length
                                                }
                                            />
                                        )
                                    ) : (
                                        <FormikAssignedSupplierSelect
                                            name="supplierId"
                                            customerIds={customerIdsForAssignedSuppliers}
                                            label={translate('contracts.form.assignedSupplier')}
                                            description={translate('contracts.form.assignedSupplierDescription')}
                                            placeholder={translate('contracts.form.assignedSupplierPlaceholder')}
                                            disabled={
                                                isSuperAdmin
                                                    ? !values.customerGroupId ||
                                                      isCustomerGroupLoading ||
                                                      !customerIdsForAssignedSuppliers.length
                                                    : !customerIdsForAssignedSuppliers.length
                                            }
                                        />
                                    )}
                                    {!isEditFlow ? (
                                        <Callout
                                            description={
                                                <InteractiveTranslation
                                                    translation={translate(
                                                        'contracts.form.assignedSupplierSupportCallout'
                                                    )}
                                                    onClick={openFreshchat}
                                                />
                                            }
                                        />
                                    ) : null}
                                </Box>

                                <Box display="flex" flexDirection="column" gap={16}>
                                    <Text as="h2" variant="subtitle1">
                                        {translate('contracts.form.sections.scope')}
                                    </Text>
                                    <FormikCustomerCompanyMultiSelect
                                        name="customerIds"
                                        groupId={isSuperAdmin ? values.customerGroupId : undefined}
                                        label={translate('contracts.form.company')}
                                        description={translate('contracts.form.companyDescription')}
                                        placeholder={translate('contracts.form.companyPlaceholder')}
                                        disabled={isSuperAdmin && !values.customerGroupId}
                                    />
                                    {!isEditFlow ? (
                                        <Box display="flex" flexDirection="column" gap={8} alignItems="flex-start">
                                            <TextButton type="button" onClick={openVesselAccessDrawer}>
                                                {translate(
                                                    values.excludedVesselIds.length
                                                        ? 'contracts.vesselAccess.manageVesselAccess'
                                                        : 'contracts.vesselAccess.excludeVessels'
                                                )}
                                            </TextButton>
                                            {values.excludedVesselIds.length ? (
                                                <Box display="flex" flexDirection="column" gap={4}>
                                                    <Text variant="fieldLabel">
                                                        {translate('contracts.vesselAccess.excludedVesselsCount', {
                                                            count: values.excludedVesselIds.length.toString(),
                                                        })}
                                                    </Text>
                                                    <Text variant="small" color="foreground.muted">
                                                        {excludedVesselNames || Characters.EN_DASH}
                                                    </Text>
                                                </Box>
                                            ) : null}
                                        </Box>
                                    ) : null}
                                    <FormikFormattedNumberInput
                                        name="minimumVolume"
                                        label={translate('contracts.form.minimumVolume')}
                                        placeholder={translate('contracts.form.minimumVolumePlaceholder')}
                                        allowNegativeValue={false}
                                        allowDecimals={false}
                                        trailingVisual={<Text variant="small">L</Text>}
                                    />
                                </Box>

                                <Box display="flex" flexDirection="column" gap={16}>
                                    <Box display="flex" flexDirection="column" gap={8}>
                                        <Text as="h2" variant="subtitle1">
                                            {translate('contracts.form.sections.validity')}
                                        </Text>
                                        <Text variant="small" color="foreground.muted">
                                            {translate('contracts.form.validityDescription')}
                                        </Text>
                                    </Box>

                                    <Box display="flex" alignItems="flex-start" gap={8}>
                                        <Box flex={1}>
                                            <FormikDatePicker
                                                name="validFrom"
                                                label={translate('contracts.form.startDate')}
                                                placeholder={translate('contracts.priceLists.startDatePlaceholder')}
                                                isClearable={false}
                                            />
                                        </Box>
                                        <Box display="flex" alignItems="center" paddingTop={36}>
                                            <IconArrowRight width={16} height={16} />
                                        </Box>
                                        <Box flex={1}>
                                            <FormikDatePicker
                                                name="validUntil"
                                                label={translate('contracts.form.endDate')}
                                                placeholder={translate('contracts.validity.evergreen')}
                                                isClearable
                                            />
                                        </Box>
                                    </Box>

                                    {values.validUntil ? (
                                        <FormikSwitch
                                            name="evergreen"
                                            label={translate('contracts.form.evergreen')}
                                            description={translate('contracts.form.evergreenDescription')}
                                        />
                                    ) : null}

                                    <Box display="flex" flexDirection="column" gap={8}>
                                        <Text variant="fieldLabel">
                                            {translate('contracts.priceLists.effectiveDateMode')}
                                        </Text>
                                        <FormikRadioGroup name="effectiveDateMode">
                                            <RadioGroupItem>
                                                <Radio value={EffectiveDateMode.DELIVERY_DATE} />
                                                <Label>
                                                    {translate('contracts.priceLists.effectiveDateMode.deliveryDate')}
                                                </Label>
                                                <Description>
                                                    {translate(
                                                        'contracts.priceLists.effectiveDateMode.deliveryDateDescription'
                                                    )}
                                                </Description>
                                            </RadioGroupItem>
                                            <RadioGroupItem>
                                                <Radio value={EffectiveDateMode.ORDER_DATE} />
                                                <Label>
                                                    {translate('contracts.priceLists.effectiveDateMode.orderDate')}
                                                </Label>
                                                <Description>
                                                    {translate(
                                                        'contracts.priceLists.effectiveDateMode.orderDateDescription'
                                                    )}
                                                </Description>
                                            </RadioGroupItem>
                                        </FormikRadioGroup>
                                    </Box>
                                </Box>
                            </>
                        ) : null}
                    </Box>

                    {showFormContent && showPriceLists ? (
                        <Box display="flex" flexDirection="column" gap={16}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={8}
                                inert={isPriceListEditing || isPriceListBusy}
                                style={isPriceListEditing ? dimmedStyle : undefined}
                            >
                                <Text as="h2" variant="subtitle1">
                                    {translate('contracts.priceLists.title')}
                                </Text>
                                <Text variant="small" color="foreground.muted">
                                    {translate('contracts.priceLists.description')}
                                </Text>
                            </Box>

                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={8}
                                inert={isPriceListEditing || isPriceListBusy}
                                style={isPriceListEditing ? dimmedStyle : undefined}
                            >
                                <Text variant="fieldLabel">{translate('contracts.priceLists.activationMode')}</Text>
                                <FormikRadioGroup name="priceListActivationMode">
                                    <RadioGroupItem>
                                        <Radio value="AUTO" />
                                        <Label>{translate('contracts.priceLists.activationMode.automatic')}</Label>
                                        <Description>
                                            {translate('contracts.priceLists.activationMode.automaticDescription')}
                                        </Description>
                                    </RadioGroupItem>
                                    <RadioGroupItem>
                                        <Radio value="MANUAL" />
                                        <Label>{translate('contracts.priceLists.activationMode.manual')}</Label>
                                        <Description>
                                            {translate('contracts.priceLists.activationMode.manualDescription')}
                                        </Description>
                                    </RadioGroupItem>
                                </FormikRadioGroup>
                            </Box>

                            <FormikPriceListSection
                                groupId={priceListGroupId}
                                activationMode={values.priceListActivationMode}
                                onEditingChange={setIsPriceListEditing}
                                onBusyChange={setIsPriceListBusy}
                            />
                        </Box>
                    ) : null}

                    {showFormContent ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            rowGap={48}
                            inert={isPriceListEditing || isPriceListBusy}
                            style={isPriceListEditing ? dimmedStyle : undefined}
                        >
                            <Box display="flex" flexDirection="column" gap={16}>
                                <Text as="h2" variant="subtitle1">
                                    {translate('contracts.form.sections.paymentAndConditions')}
                                </Text>

                                <Box display="grid" gridTemplateColumns="1fr 100px" gap={8}>
                                    <FormikEnumComboBox
                                        name="paymentTermReference"
                                        label={translate('contracts.form.paymentTerms')}
                                        placeholder={translate('contracts.form.paymentTermsPlaceholder')}
                                        enumType="paymentTermReference"
                                    />
                                    <FormikFormattedNumberInput
                                        name="paymentTermReferenceDays"
                                        label={translate('contracts.form.days')}
                                        allowNegativeValue={false}
                                        allowDecimals={false}
                                        disabled={values.paymentTermReference === PaymentTermReference.PREPAYMENT}
                                    />
                                </Box>

                                <FormikInput
                                    name="gtc"
                                    label={translate('contracts.form.gtc')}
                                    description={translate('contracts.form.gtcDescription')}
                                    placeholder={translate('contracts.form.gtcPlaceholder')}
                                />
                            </Box>
                            <FormikDebug />
                        </Box>
                    ) : null}
                </Box>
            </DrawerContentLayout>
        </Form>
    );
};
